import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
    Clock,
    Plus,
    Edit2,
    Trash2,
    CheckCircle,
    XCircle,
    StopCircle,
} from 'lucide-react'
import { Typogaphy } from 'vnetwork-ui'

import Paper from '@/components/Paper'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useConfirmation } from '@/contexts/confirmation'
import { QUERY_KEYS, QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import {
    getServiceDowntimeLogs,
    getServiceSLA,
    deleteDowntimeLog,
    endDowntime,
} from '@/modules/Service/services/sla'
import {
    SLADowntimeLog,
    ServiceSLA,
    DowntimeType,
    DowntimeCause,
    DOWNTIME_TYPE_LABELS,
    DOWNTIME_CAUSE_LABELS,
    SLA_TIER_CONFIG,
    SLATier,
} from '@/modules/Service/types/sla'
import DowntimeFormModal from '../Form'
import EndDowntimeModal from '../EndDowntimeModal'
import UptimeStats from '../UptimeStats'
import { formatFromUTC } from '@/utils/function'

dayjs.extend(utc)

interface DowntimeListProps {
    serviceId: number
}

const getDowntimeTypeBadge = (type: DowntimeType) => {
    const styles: Record<DowntimeType, string> = {
        unplanned: 'bg-red-100 text-red-700 border-red-200',
        planned: 'bg-blue-100 text-blue-700 border-blue-200',
        maintenance: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        emergency: 'bg-orange-100 text-orange-700 border-orange-200',
    }
    return styles[type] || styles.unplanned
}

const getDowntimeCauseBadge = (cause: DowntimeCause) => {
    const styles: Record<DowntimeCause, string> = {
        provider: 'bg-red-100 text-red-700 border-red-200',
        customer: 'bg-gray-100 text-gray-700 border-gray-200',
        third_party: 'bg-purple-100 text-purple-700 border-purple-200',
        force_majeure: 'bg-orange-100 text-orange-700 border-orange-200',
        scheduled: 'bg-green-100 text-green-700 border-green-200',
    }
    return styles[cause] || styles.provider
}

const DowntimeList: React.FC<DowntimeListProps> = ({ serviceId }) => {
    const { t } = useTranslation('services')
    const { setConfirmation } = useConfirmation()

    const [isFormOpen, setIsFormOpen] = useState(false)
    const [editingDowntime, setEditingDowntime] =
        useState<SLADowntimeLog | null>(null)
    const [isEndModalOpen, setIsEndModalOpen] = useState(false)
    const [endingDowntime, setEndingDowntime] = useState<SLADowntimeLog | null>(
        null
    )

    // Fetch downtime logs
    const {
        data: logsData,
        isLoading,
        refetch,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SLA_DOWNTIME, serviceId],
        queryFn: () =>
            getServiceDowntimeLogs(serviceId, {
                page: 1,
                page_size: 50,
                q: '',
            }),
    })

    // Fetch SLA for resolution time
    const { data: slaData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, serviceId, 'sla'],
        queryFn: () => getServiceSLA(serviceId),
        enabled: !!serviceId && serviceId > 0,
    })

    const sla = slaData?.result as ServiceSLA | undefined
    const tier = sla?.sla_template?.template_tier as SLATier | undefined
    const tierConfig = tier && tier !== 'custom' ? SLA_TIER_CONFIG[tier] : null

    // Get resolution time from SLA (template or tier default)
    const resolutionTimeMinutes =
        sla?.sla_template?.default_resolution_time ||
        tierConfig?.resolutionTimeMinutes

    // Delete mutation - also invalidate uptime stats
    const { mutate: deleteLog } = useMutationWithHandler(
        {
            mutationFn: (downtimeId: number) =>
                deleteDowntimeLog(serviceId, downtimeId),
        },
        {
            successMessage: t('sla.downtime_deleted', 'Đã xóa log downtime'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SLA_DOWNTIME_WITH_STATS,
            onSuccess: () => refetch(),
        }
    )

    // End downtime mutation - also invalidate uptime stats
    const { mutate: endDowntimeLog, isPending: isEndingDowntime } =
        useMutationWithHandler(
            {
                mutationFn: ({
                    downtimeId,
                    endTime,
                }: {
                    downtimeId: number
                    endTime: string
                }) =>
                    endDowntime(serviceId, downtimeId, {
                        downtime_end: endTime,
                    }),
            },
            {
                successMessage: t('sla.downtime_ended', 'Đã kết thúc downtime'),
                reactQueryKeys: QUERY_KEY_COMBINATIONS.SLA_DOWNTIME_WITH_STATS,
                onSuccess: () => {
                    refetch()
                    setIsEndModalOpen(false)
                    setEndingDowntime(null)
                },
            }
        )

    const logs = logsData?.results || []

    const handleCreate = () => {
        setEditingDowntime(null)
        setIsFormOpen(true)
    }

    const handleEdit = (log: SLADowntimeLog) => {
        setEditingDowntime(log)
        setIsFormOpen(true)
    }

    const handleDelete = (log: SLADowntimeLog) => {
        setConfirmation({
            open: true,
            title: t('sla.delete_downtime_title', 'Xóa Downtime Log'),
            description: t(
                'sla.delete_downtime_description',
                'Bạn có chắc muốn xóa log này?'
            ),
            onConfirm: () => deleteLog(log.downtime_log_id),
        })
    }

    const handleEndDowntime = (log: SLADowntimeLog) => {
        setEndingDowntime(log)
        setIsEndModalOpen(true)
    }

    const handleConfirmEndDowntime = (endTime: string) => {
        if (endingDowntime) {
            endDowntimeLog({
                downtimeId: endingDowntime.downtime_log_id,
                endTime,
            })
        }
    }

    const isOngoing = (log: SLADowntimeLog) => !log.downtime_end

    const formatDuration = (minutes?: number) => {
        if (!minutes) return '-'
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        if (hours > 0) {
            return `${hours}h ${mins}m`
        }
        return `${mins}m`
    }

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-32 rounded-lg" />
                <Skeleton className="h-64 rounded-lg" />
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Uptime Stats */}
            <UptimeStats serviceId={serviceId} />

            {/* Downtime Logs */}
            <Paper className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-orange-500" />
                        <Typogaphy className="font-semibold text-gray-900">
                            {t('sla.downtime_logs', 'Downtime Logs')}
                        </Typogaphy>
                    </div>
                    <Button size="sm" onClick={handleCreate}>
                        <Plus className="w-4 h-4 mr-1" />
                        {t('sla.log_downtime', 'Ghi nhận Downtime')}
                    </Button>
                </div>

                {logs.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-8 h-8 text-green-300 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">
                            {t(
                                'sla.no_downtime_logs',
                                'Chưa có downtime nào được ghi nhận'
                            )}
                        </p>
                    </div>
                ) : (
                    <Table className="text-xs">
                        <TableHeader>
                            <TableRow className="h-8">
                                <TableHead className="py-2 px-2 text-xs">
                                    {t('sla.type', 'Loại')}
                                </TableHead>
                                <TableHead className="py-2 px-2 text-xs">
                                    {t('sla.cause', 'Nguyên nhân')}
                                </TableHead>
                                <TableHead className="py-2 px-2 text-xs">
                                    {t('sla.start', 'Bắt đầu')}
                                </TableHead>
                                <TableHead className="py-2 px-2 text-xs">
                                    {t('sla.end', 'Kết thúc')}
                                </TableHead>
                                <TableHead className="py-2 px-2 text-xs">
                                    {t('sla.duration', 'Thời lượng')}
                                </TableHead>
                                <TableHead className="py-2 px-2 text-xs text-center">
                                    {t('sla.counts_sla', 'Tính SLA')}
                                </TableHead>
                                <TableHead className="py-2 px-2 text-xs">
                                    {t('sla.description', 'Mô tả')}
                                </TableHead>
                                <TableHead className="py-2 px-1 w-24"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow
                                    key={log.downtime_log_id}
                                    className="h-9"
                                >
                                    <TableCell className="py-1.5 px-2">
                                        <Badge
                                            variant="outline"
                                            className={`text-[10px] px-1.5 py-0.5 ${getDowntimeTypeBadge(
                                                log.downtime_type
                                            )}`}
                                        >
                                            {
                                                DOWNTIME_TYPE_LABELS[
                                                    log.downtime_type
                                                ]
                                            }
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="py-1.5 px-2">
                                        {log.downtime_cause && (
                                            <Badge
                                                variant="outline"
                                                className={`text-[10px] px-1.5 py-0.5 ${getDowntimeCauseBadge(
                                                    log.downtime_cause
                                                )}`}
                                            >
                                                {
                                                    DOWNTIME_CAUSE_LABELS[
                                                        log.downtime_cause
                                                    ]
                                                }
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-1.5 px-2 text-xs text-gray-600">
                                        {formatFromUTC(log.downtime_start)}
                                    </TableCell>
                                    <TableCell className="py-1.5 px-2 text-xs">
                                        {isOngoing(log) ? (
                                            <Badge className="bg-red-100 text-red-700 border-red-200 animate-pulse text-[10px] px-1.5 py-0.5">
                                                Đang diễn ra
                                            </Badge>
                                        ) : (
                                            <span className="text-gray-600">
                                                {formatFromUTC(
                                                    log.downtime_end
                                                )}
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="py-1.5 px-2 font-medium text-xs">
                                        {isOngoing(log) ? (
                                            <span className="text-red-600">
                                                {formatDuration(
                                                    dayjs().diff(
                                                        dayjs.utc(
                                                            log.downtime_start
                                                        ),
                                                        'minute'
                                                    )
                                                )}
                                            </span>
                                        ) : (
                                            formatDuration(log.downtime_minutes)
                                        )}
                                    </TableCell>
                                    <TableCell className="py-1.5 px-2 text-center">
                                        {log.counts_against_sla ? (
                                            <XCircle className="w-3.5 h-3.5 text-red-500 mx-auto" />
                                        ) : (
                                            <CheckCircle className="w-3.5 h-3.5 text-green-500 mx-auto" />
                                        )}
                                    </TableCell>
                                    <TableCell className="py-1.5 px-2 text-xs text-gray-500 max-w-[150px] truncate">
                                        {log.downtime_description || '-'}
                                    </TableCell>
                                    <TableCell className="py-1.5 px-1">
                                        <div className="flex items-center gap-0.5">
                                            {isOngoing(log) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleEndDowntime(log)
                                                    }
                                                    className="h-7 w-7 p-0 text-orange-600 hover:text-orange-700"
                                                >
                                                    <StopCircle className="w-3.5 h-3.5" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleEdit(log)}
                                                className="h-7 w-7 p-0"
                                            >
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    handleDelete(log)
                                                }
                                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Form Modal */}
            <DowntimeFormModal
                isOpen={isFormOpen}
                onClose={() => {
                    setIsFormOpen(false)
                    setEditingDowntime(null)
                }}
                onSuccess={() => {
                    setIsFormOpen(false)
                    setEditingDowntime(null)
                    refetch()
                }}
                serviceId={serviceId}
                downtime={editingDowntime}
            />

            {/* End Downtime Modal */}
            <EndDowntimeModal
                isOpen={isEndModalOpen}
                onClose={() => {
                    setIsEndModalOpen(false)
                    setEndingDowntime(null)
                }}
                onConfirm={handleConfirmEndDowntime}
                downtime={endingDowntime}
                isLoading={isEndingDowntime}
                resolutionTimeMinutes={resolutionTimeMinutes}
            />
        </div>
    )
}

export default DowntimeList
